import posthog from "posthog-js"
// Dead-click capture ships INSIDE our own bundle (about 8.5 KB compressed):
// this registers the module on window.__PosthogExtensions__, so posthog-js
// never fetches /ingest/static/dead-clicks-autocapture.js, the file ad
// blockers match by name. That fetch failing was the only reason dead clicks
// were ever switched off.
import "posthog-js/dist/dead-clicks-autocapture"
import {
  captureMoneyPathUnhandledExceptions,
  isPrivateMessagingObservabilityPath,
  keepCredentialUrlOutOfReferrers,
  releaseCredentialReferrerPolicy,
  maskReplayNetworkRequest,
  repairStoredFirstVisit,
  scrubBrowserExceptionEvent,
  scrubPosthogEvent,
  SECRET_URL_PARAMS,
} from "@/lib/observability/posthog-scrub"
import {
  adoptAnalyticsPersonId,
  captureFirstTouch,
  captureInternalTrafficFlag,
  isInternalTrafficDevice,
  isSyntheticInspectionBot,
  syncPosthogSessionCookie,
} from "@/lib/analytics"
import { captureAdClickId } from "@/lib/leads/entry-attribution"
import { captureAcquisitionContext } from "@/lib/acquisition/context"

// Client-side PostHog init (Next.js 15.3+ instrumentation-client pattern:
// never combine with a PostHogProvider). Ingestion routes through the
// /ingest proxy in proxy.ts so ad-blockers don't eat events.
//
// Deliberate posture (see context/analytics.md):
// - persistence "localStorage+cookie": analytics cookies are on site-wide.
//   PostHog stores the same distinct_id in both
//   localStorage and a first-party cookie, so identity survives a cleared
//   localStorage and stays flicker-free for any future A/B test. The cookie
//   policy (see app/(marketing)/cookie-policy) documents this analytics
//   cookie plainly, without naming the vendor.
// - capture_exceptions is ON in production: PostHog is the one error tracker,
//   browser and server (context/error-pipeline.md). Unhandled errors and
//   unhandled rejections are captured, console errors are not, and the
//   before_send below drops browser noise and marks money-path exceptions.
// - session replay text inputs stay UNMASKED (no maskAllInputs/maskInputOptions
//   below): the owner needs to see what a visitor actually typed before
//   abandoning /apply to diagnose the step-1 drop-off. Only PostHog admins can
//   view replays. Network requests are still scrubbed (query strings + bodies
//   stripped) and the private-messaging routes are excluded entirely.
const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
const privateMessagingRouteAtLoad =
  typeof window !== "undefined" &&
  isPrivateMessagingObservabilityPath(window.location.pathname)
// Two device classes are excluded from analytics so they never pollute the
// tiny real funnel data (see context/analytics.md "Internal traffic filtering"):
//   1. the automated weekly funnel-inspection bot (it self-marks before
//      navigating), and
//   2. OUR OWN internal devices - the owner's + Matthew's - which opt out for
//      good by visiting once with ?ca_internal=1 (owner, 2026-07-18: "we should
//      ideally take our own internal traffic out actually"). Every real visitor
//      is still recorded.
// Skipping init entirely for an excluded device means it sends zero PostHog
// network calls, ever. captureInternalTrafficFlag() must run first so a fresh
// ?ca_internal=1 takes effect on this very page load.
captureInternalTrafficFlag()
const excludedDevice = isSyntheticInspectionBot() || isInternalTrafficDevice()
// On a page whose URL holds a live credential (/pay/return's Stripe client
// secret, a sign-in token_hash), every request from here on names only our
// origin as its referrer, so the SDK's script loads and unload beacon through
// /ingest never hand PostHog the full URL. See lib/observability/posthog-scrub.ts.
keepCredentialUrlOutOfReferrers(document, window.location.href)

// Next calls this as a client-side step starts, Back and Forward included;
// stepping to a URL without a credential lifts that policy, so later page
// loads name the real page again, and stepping back onto one tightens it again.
export function onRouterTransitionStart(url: string) {
  releaseCredentialReferrerPolicy(document, new URL(url, window.location.href).href)
}

if (token && !privateMessagingRouteAtLoad && !excludedDevice) {
  // On a pay or apply page every unhandled error and rejection is captured
  // through posthog.captureException, past posthog-js's own per-browser limit
  // (ten per exception type, then one every ten seconds), and the before_send
  // drops the SDK's autocaptured twin once this is installed. Production
  // only, like capture_exceptions.
  const moneyPathListener =
    process.env.NODE_ENV === "production" &&
    captureMoneyPathUnhandledExceptions(window, (error, properties) => posthog.captureException(error, properties))
  // The session recorder is fetched by posthog-js as
  // /ingest/static/posthog-recorder.js, a filename ad blockers match, which
  // loses about 4% of recordings. Start loading our own bundled copy now, as a
  // separate hashed chunk that no filter names: posthog-js only starts
  // recording after its remote config answers, and finds the recorder already
  // registered whenever this chunk wins that race, so it fetches nothing. When
  // the chunk loses, posthog-js fetches the file exactly as before, so this can
  // only recover recordings, never lose one. It is a dynamic import rather than
  // a static one because the recorder is about 67 KB compressed and a static
  // import would add that to the first load of every page, landing pages
  // included. Production only, like replay itself.
  if (process.env.NODE_ENV === "production") {
    void import("posthog-js/dist/posthog-recorder").catch(() => undefined)
  }
  // Initialize immediately on every page load (no deferral): PostHog must be
  // wired up everywhere and never miss an early event/pageview (owner directive).
  // Next runs this synchronous instrumentation after HTML loads and before
  // React hydrates. Async imports are not awaited. First-load bundle, network,
  // and evaluation performance still need measurement. Ingestion is first-party
  // via /ingest.
  posthog.init(token, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    // The first $pageview of a visit used to sit in posthog-js's request queue
    // for up to 3 seconds (the default flush interval). The SDK already sends
    // the queue by beacon on pagehide, so a Back tap or a closed tab was never
    // the loss; a page that never gets pagehide is (switching apps, closing an
    // in-app browser, the OS discarding the tab). 250 ms is the SDK's
    // documented minimum ("apps with very short visits"). It sends the same
    // events sooner. It also delivers short visits from automated browsers that
    // pass the SDK's user-agent filter; the project's test-account filter
    // removes the ones PostHog classifies as bots.
    request_queue_config: { flush_interval_ms: 250 },
    // "2026-05-30" (from "2026-01-30"): rage clicks ignore text selection and
    // number steppers, visits from the Google app are labelled as their own
    // browser, flag storage is split out of the main entry, persistence writes
    // are debounced, and canvas recording is captured at 0.6 scale. Stop here:
    // "2026-06-25" changes replay network capture (streamed request bodies),
    // which the maskCapturedNetworkRequestFn below was not written against.
    // INP and LCP carry attribution by default at this library version; CLS
    // attribution stays off because posthog-js warns it leaks detached DOM
    // nodes in single-page apps.
    defaults: "2026-05-30",
    persistence: "localStorage+cookie",
    // Taps on things that look clickable but do nothing (his approval: "Record
    // taps on things that look clickable but do nothing, like a price or an
    // offer card, so confusing spots show up"). The module is bundled by the
    // import at the top of this file, so no ad blocker can stop it loading.
    // Feeds the "Friction: rage + dead clicks by page" chart, the Click
    // friction action and the "Rage and dead clicks" AI watcher.
    capture_dead_clicks: true,
    // posthog-js keeps the first page a browser ever loaded and sends it outside
    // before_send: as the person's $initial_* properties on every /flags
    // request, and in its first-party cookie. Masking the credential parameters
    // at the source keeps them out of that copy and every other one
    // ($current_url, $session_entry_url, heatmap URLs). posthog-js always adds
    // the ad click ids to this list, so gclid, fbclid and the rest arrive as
    // <masked>: present, which is all attribution reads (the daily pulse's paid
    // split and PostHog's channel types test that a click id is set), while the
    // UTMs, the referrer and the page path arrive untouched.
    mask_personal_data_properties: true,
    custom_personal_data_properties: [...SECRET_URL_PARAMS],
    // Explicit heatmap capture: record the $heatmap click/scroll stream on
    // every page (rage clicks and dead clicks are captured separately, above).
    // The project's heatmaps_opt_in is also true, but
    // setting this here makes capture explicit and self-documenting instead of
    // depending on a remote-config round-trip. Powers the saved heatmaps +
    // the in-app heatmap toolbar across the whole funnel.
    enable_heatmaps: true,
    // PostHog's survey UI never renders on the site (owner, 2026-07-17: "the
    // survey should not be through PostHog, it should be through our website").
    // Any visitor survey is built as our own UI; the PostHog widget stays off.
    disable_surveys: true,
    // Error tracking: production only, the same way the server half is, so a
    // local HMR transient never files a GitHub issue. This gates only the
    // autocapture; posthog.captureException ignores it, so the before_send
    // below drops every $exception outside production as well. Pay and apply
    // pages capture through the listener above instead of this autocapture's
    // per-browser limit.
    capture_exceptions:
      process.env.NODE_ENV === "production"
        ? { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false }
        : false,
    // Session replay is a PRODUCTION capability: the recorder is a separate
    // lazily-loaded module (/ingest/static/posthog-recorder.js), and on
    // localhost a blocked or interrupted load throws "[SessionRecording]
    // could not load recorder" into the dev overlay on every page, while the
    // only thing a dev recording could capture is our own localhost session,
    // which is noise in the replay list. Same trade as capture_dead_clicks
    // above: turn the module off where its output has no value instead of
    // shipping a console error. Real-visitor replays are unaffected.
    disable_session_recording: process.env.NODE_ENV === "development",
    session_recording: {
      // posthog-js also runs the page URL of every replay (the rrweb meta
      // href, its $pageview and $url_changed markers) through this function,
      // so dropping the query string and fragment there is what keeps a
      // sign-in token_hash or a Stripe client secret out of the recording's
      // URLs. The token's hidden form field carries the ph-no-capture block
      // class for the page snapshot itself.
      maskCapturedNetworkRequestFn: maskReplayNetworkRequest,
    },
    // Every event: drop it on the private-messaging routes, and otherwise mask
    // the value of a sign-in token_hash or a Stripe client secret wherever
    // the page URL was copied ($current_url, $referrer, $initial_* and
    // $session_entry_* properties, heatmap keys, autocapture hrefs). A
    // $exception event is dropped outside production and when it is browser
    // noise (a translated page's hydration mismatch included, read from the
    // document) or the autocaptured twin of an error the money-page listener
    // already sent; otherwise it has secret-shaped values in its message
    // redacted, and carries ca_money_path on a pay or apply page so no rate
    // limit drops it. See lib/observability/posthog-scrub.ts.
    before_send: (event) =>
      isPrivateMessagingObservabilityPath(window.location.pathname)
        ? null
        : scrubBrowserExceptionEvent(scrubPosthogEvent(event), window.location.pathname, {
            moneyPathListener,
            document,
          }),
    debug: process.env.NODE_ENV === "development",
  })
  // A browser that stored its first visit before the masking above keeps that
  // copy for good; mask it now, before the first /flags request is built.
  repairStoredFirstVisit(posthog)
  // There is deliberately NO explicit landing $pageview capture here.
  //
  // posthog-js already captures it. Under the `defaults` date above,
  // capture_pageview is on, which fires a $pageview on the initial document
  // load AND on every client-side navigation. Calling
  // posthog.capture("$pageview") on top of that double-counted EVERY pageview,
  // on EVERY page, in EVERY browser, roughly 0.1s apart.
  //
  // Measured in PostHog project 465047: of 754 sessions carrying a sub-second
  // duplicate pair, 618 shared a single $initialization_time, so both events
  // came from ONE document load with ONE SDK init. Same $current_url, same
  // window, and the same rate on /, /apply, /online-dating-concierge,
  // /testimonials, /sign-in and article pages. It was never webview-specific:
  // only 31 of 421 duplicate /apply sessions were the Facebook in-app browser,
  // the rest were Mobile Safari, Chrome, Samsung Internet, Brave, Edge and
  // Firefox.
  //
  // The sessionStorage + prerenderingchange guard that used to sit here could
  // never have worked, which is worth stating so it does not get rebuilt: it
  // only suppressed a SECOND call to its own helper within one tab, and that
  // helper ran exactly once per document load, so on a normal visit it was a
  // no-op. It had no way to reach posthog-js's own capture, which was the
  // actual duplicate. Measured effect: multi-pageview /apply sessions went
  // 78.2% before it shipped to 73.5% after, and the median gap inside a pair
  // stayed flat at 0.102s to 0.105s.
  //
  // If a landing pageview ever has to be captured by hand again, turn
  // capture_pageview OFF in the init above in the same change, or the double
  // comes straight back.

  // THE ANALYTICS IDENTITY SPINE, both directions.
  //
  // Out: this browser's PostHog session id goes into a first-party cookie, so
  // a server-side event fired during any request can name the visit, and the
  // session replay, that produced it. Refreshed on a timer because a session id
  // rotates after half an hour of quiet, and /apply is one page a man can sit
  // on for longer than that. Re-writing the same value also renews the cookie's
  // own lifetime, which is what keeps a long form fill attributable.
  //
  // In: the immutable person id the server minted at first durable lead capture
  // comes back in a cookie and is stamped onto the PostHog person, so events
  // fired weeks apart under three different distinct ids still join to one man.
  // See web/lib/leads/analytics-identity.ts for why it is never an identify().
  syncPosthogSessionCookie()
  adoptAnalyticsPersonId()
  setInterval(syncPosthogSessionCookie, 60_000)
}

// Remember how this browser first found us (referrer/UTM/landing page), so
// /apply can stamp the source onto each lead. First touch only; PostHog owns
// the deep analytics side. Runs regardless of the PostHog token.
captureFirstTouch()
// Beside it, and for the same reason it exists: captureFirstTouch records only
// that an ad click id was PRESENT and discards the value, so a paid lead could
// never be traced to the ad that produced it. First one wins, same as above.
captureAdClickId()
// And the acquisition envelope for THIS visit: the id every CTA click carries
// and the two money-path writers bind, so "which page produced this sale" has
// a durable join instead of only a PostHog event property. sessionStorage
// rather than localStorage, because it describes this visit rather than the
// browser's first ever one. See web/lib/acquisition/context.ts.
captureAcquisitionContext()

// Meta Pixel: ads measurement ONLY (PostHog keeps product analytics and
// errors). Inert until NEXT_PUBLIC_META_PIXEL_ID is set: no script load, no
// cookies, nothing sent. Never initializes on /admin routes. Base PageView + offering
// ViewContent fire from here; the funnel events (StartApplication / Lead /
// SubmitApplication) forward through track() in web/lib/analytics.ts, deduped
// against the server Conversions API events. See context/meta-ads.md.
// Gated on the SAME excludedDevice test as PostHog above, and for a sharper
// reason: the weekly funnel-inspection bot fills the /apply email step with an
// internal address, so an ungated pixel fires a browser `Lead` for it every
// week. The server half returns early on isInternalEmail(), so that Lead has no
// server twin to dedup against and Meta counts it outright: a fake conversion
// against a real funnel that only sees single digits a week.
import { initMetaPixel } from "@/lib/meta-pixel"

if (!excludedDevice) initMetaPixel()
